/**
 * Photon Trace — the approved luminous-trail treatment for Trace Pointes.
 *
 * Ported from the owner-approved handoff (`docs/trace-pointes/handoff/`,
 * reference module `photon-trace.js`, 2026-10-30) and adapted to this
 * repository's data (frame-indexed normalized pose points, see
 * `hooks/useTrajectoryTrail.ts`) and renderer (`TrajectoryTrailOverlay.tsx`).
 *
 * What this module owns
 *  - ONE typed, frozen style object (`PHOTON_TRACE_STYLE`): every visual
 *    constant of the ribbon lives here. All dimensions are CSS pixels; the
 *    device pixel ratio only affects the canvas backing store.
 *  - `prepareRuns`: splits timestamped observations into confidence-valid,
 *    time-continuous runs. It never sorts, interpolates across, or bridges a
 *    lost track.
 *  - `clipRun`: exact temporal clipping to [start, end] with interpolation
 *    ONLY inside an existing run (clip boundaries, never gaps).
 *  - `drawPhotonTrace`: four narrow light strips on the observed centreline
 *    (bloom, halo, coloured body, ice-white core), a tapered beginning, age →
 *    opacity + colour, and a small head glow + tangent flare that is drawn
 *    ONLY while a run is active at the current media time (no stale head).
 *
 * What it does NOT own: video, display transforms (callers pass CSS-pixel
 * coordinates already mapped through the viewer's letterbox transform),
 * requestAnimationFrame, canvas sizing/clearing, UI state, scoring data.
 * It saves and restores the context state it touches.
 *
 * Deterministic: same runs + time + style ⇒ same draw calls. No randomness,
 * textures, shaders, CSS filters, Path2D (absent in jsdom 27) or third-party
 * dependencies.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TraceObservation {
  /** Media timestamp in seconds, same time domain as the viewer. */
  t: number;
  /** CSS pixels relative to the canvas top-left, after display transform. */
  x: number;
  y: number;
  /** Finite 0..1 confidence. Below `minConfidence` the sample splits the run. */
  confidence: number;
  /** Explicit discontinuity / identity change — split regardless of time. */
  breakBefore?: boolean;
}

export interface TraceSample {
  t: number;
  x: number;
  y: number;
}

export type TraceRun = TraceSample[];

export type RGB = readonly [number, number, number];

export interface PhotonTraceStyle {
  /** Fine ice-white inner core. */
  corePx: number;
  /** Narrow luminous coloured body. */
  ribbonPx: number;
  /** Quiet near halo. */
  haloPx: number;
  /** Very faint wide bloom. */
  bloomPx: number;
  bloomAlpha: number;
  haloAlpha: number;
  ribbonAlpha: number;
  coreAlpha: number;
  /** Temporal brightness easing exponent (age → opacity). */
  fadePower: number;
  /** Radial head-light falloff radius; the bright centre is much smaller. */
  tipRadiusPx: number;
  /** Tangent-aligned flare behind the head, in CSS px (back, forward, width). */
  flareBackPx: number;
  flareForwardPx: number;
  flareWidthPx: number;
  /** Approved single-track palette: violet old tail → cyan leading light. */
  tailRGB: RGB;
  headRGB: RGB;
  /** Ice-white core colour. */
  coreRGB: RGB;
  /**
   * How far a per-track leading colour is pulled toward `tailRGB` for its
   * old tail (0 = track colour everywhere, 1 = approved violet). Keeps track
   * identity (the body/head stays the track's palette colour) while the
   * history still quietens toward the approved violet.
   */
  tailMix: number;
  /** Display-space resample spacing for the strip centreline, CSS px. */
  resampleSpacingPx: number;
  /** Hard cap on centreline samples per run per draw (bounded work). */
  maxSamplesPerRun: number;
  /** Age/colour quantisation bands per strip (batched fills). */
  shadeBands: number;
  /**
   * Fraction of the visible window over which the width eases in at the
   * oldest end (tapered beginning). Handoff: sin(min(1, u·9)·π/2) ⇒ 1/9.
   */
  taperWindowFraction: number;
}

/** The approved baseline (handoff INTEGRATION.md "Rendering treatment"). */
export const PHOTON_TRACE_STYLE: Readonly<PhotonTraceStyle> = Object.freeze({
  corePx: 0.65,
  ribbonPx: 2.2,
  haloPx: 11,
  bloomPx: 22,
  bloomAlpha: 0.018,
  haloAlpha: 0.055,
  ribbonAlpha: 0.72,
  coreAlpha: 0.95,
  fadePower: 0.8,
  tipRadiusPx: 8,
  flareBackPx: 8,
  flareForwardPx: 3,
  flareWidthPx: 0.7,
  tailRGB: Object.freeze([110, 92, 255] as const),
  headRGB: Object.freeze([65, 222, 255] as const),
  coreRGB: Object.freeze([221, 253, 255] as const),
  tailMix: 0.65,
  resampleSpacingPx: 2,
  maxSamplesPerRun: 2400,
  shadeBands: 24,
  taperWindowFraction: 1 / 9,
});

/**
 * Run-splitting policy for this product's pose stream. The renderer's
 * observations come from `useTrajectoryTrail`, which has ALREADY dropped
 * frames below the detector confidence gate (0.5, shared with the scoring
 * evaluators) and frames that fail the phantom-leap gate (0.09 normalized
 * units per frame). A dropped frame is therefore a HOLE in the frame sequence,
 * not a low-confidence sample. Up to `maxBridgedDroppedFrames` consecutive
 * holes are drawn through (one strip across ~100 ms at 30 fps — the same
 * threshold the earlier confidence-thinning used); longer holes split the run
 * and nothing is drawn across them.
 */
export const PHOTON_TRACE_RUN_POLICY = Object.freeze({
  maxBridgedDroppedFrames: 3,
});

// ---------------------------------------------------------------------------
// Run preparation + temporal clipping
// ---------------------------------------------------------------------------

export interface PrepareRunsOptions {
  /** Observations below this confidence split the run (default 0.5). */
  minConfidence?: number;
  /** Gaps longer than this (seconds) split the run. Required > 0. */
  maxGapSeconds: number;
}

/**
 * Preserve acquisition order. Reject invalid / low-confidence samples and
 * split runs at rejections, explicit breaks, non-increasing timestamps and
 * excessive time gaps. Never sorts and never interpolates across a gap.
 */
export function prepareRuns(
  samples: readonly TraceObservation[],
  { minConfidence = 0.5, maxGapSeconds }: PrepareRunsOptions,
): TraceRun[] {
  if (!Number.isFinite(maxGapSeconds) || maxGapSeconds <= 0) {
    throw new RangeError('maxGapSeconds must be a positive finite number');
  }
  const runs: TraceRun[] = [];
  let run: TraceRun = [];
  const flush = () => {
    if (run.length) runs.push(run);
    run = [];
  };
  for (const p of samples) {
    if (
      !p ||
      ![p.t, p.x, p.y, p.confidence].every(Number.isFinite) ||
      p.confidence < minConfidence
    ) {
      flush();
      continue;
    }
    const prev = run[run.length - 1];
    if (p.breakBefore || (prev && (p.t <= prev.t || p.t - prev.t > maxGapSeconds))) flush();
    run.push({ t: p.t, x: p.x, y: p.y });
  }
  flush();
  return runs;
}

/** Linear interpolation inside a run at time t (t within the run's span). */
export function sampleRunAt(run: TraceRun, t: number): TraceSample {
  let lo = 0;
  let hi = run.length - 1;
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (run[mid].t < t) lo = mid + 1;
    else hi = mid;
  }
  const b = run[lo];
  const a = run[Math.max(0, lo - 1)];
  if (a.t === b.t) return { t: b.t, x: b.x, y: b.y };
  const u = (t - a.t) / (b.t - a.t);
  return { t, x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u };
}

/** Exact temporal clipping; interpolation happens only within the run. */
export function clipRun(run: TraceRun, start: number, end: number): TraceRun {
  if (!run.length || end < start) return [];
  const first = run[0];
  const last = run[run.length - 1];
  if (first.t > end || last.t < start) return [];
  const a = Math.max(start, first.t);
  const b = Math.min(end, last.t);
  if (a === b) return [sampleRunAt(run, a)];
  return [sampleRunAt(run, a), ...run.filter((p) => p.t > a && p.t < b), sampleRunAt(run, b)];
}

/**
 * Display-space resample of ONE run: subdivides long segments so no segment
 * exceeds `spacingPx`, interpolating timestamps linearly. Keeps every source
 * sample, adds no curvature (no invented loops), never crosses a run boundary,
 * and is bounded by `maxSamples`.
 */
export function resampleRun(run: TraceRun, spacingPx: number, maxSamples: number): TraceRun {
  if (run.length < 2 || !(spacingPx > 0)) return run;
  let total = 0;
  for (let i = 1; i < run.length; i++) total += Math.hypot(run[i].x - run[i - 1].x, run[i].y - run[i - 1].y);
  const budget = Math.max(run.length, Math.min(maxSamples, run.length + Math.ceil(total / spacingPx)));
  const spacing = Math.max(spacingPx, total / Math.max(1, budget - run.length));
  const out: TraceRun = [run[0]];
  for (let i = 1; i < run.length; i++) {
    const a = run[i - 1];
    const b = run[i];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    const n = Math.min(Math.floor(len / spacing), maxSamples);
    for (let k = 1; k <= n; k++) {
      const u = k / (n + 1);
      out.push({ t: a.t + (b.t - a.t) * u, x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u });
    }
    out.push(b);
    if (out.length >= maxSamples) break;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Colour helpers
// ---------------------------------------------------------------------------

export function hexToRgb(hex: string): RGB {
  const h = hex.replace('#', '');
  const v = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = Number.parseInt(v, 16);
  if (!Number.isFinite(n) || v.length !== 6) return PHOTON_TRACE_STYLE.headRGB;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function mixRgb(a: RGB, b: RGB, u: number): RGB {
  const k = Math.max(0, Math.min(1, u));
  return [
    Math.round(a[0] + (b[0] - a[0]) * k),
    Math.round(a[1] + (b[1] - a[1]) * k),
    Math.round(a[2] + (b[2] - a[2]) * k),
  ];
}

function rgba(rgb: RGB, alpha: number): string {
  return `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${alpha.toFixed(4)})`;
}

/**
 * Per-track palette: the leading light is the track's own palette colour
 * (identity preserved across multiple selected landmarks); its old tail is
 * pulled toward the approved violet. With the default cyan palette slot this
 * reproduces the approved single-track baseline.
 */
export function trackPalette(trackHex: string | null | undefined, style: Readonly<PhotonTraceStyle>): { headRGB: RGB; tailRGB: RGB } {
  const headRGB = trackHex ? hexToRgb(trackHex) : style.headRGB;
  return { headRGB, tailRGB: mixRgb(headRGB, style.tailRGB, style.tailMix) };
}

// ---------------------------------------------------------------------------
// Drawing
// ---------------------------------------------------------------------------

export interface DrawPhotonTraceOptions {
  /** Current media time, seconds. */
  time: number;
  /**
   * Start of the visible window, seconds (clip start when persisting, else
   * `time - tail`). Age/colour/taper are relative to [startTime, time].
   */
  startTime: number;
  /**
   * How long after a run's last observation the head still counts as
   * "active" (one frame duration). Beyond it the track is lost and no head is
   * drawn.
   */
  headToleranceSeconds?: number;
  /** Track identity colour (palette hex). Omit for the approved single-track look. */
  trackHex?: string | null;
  style?: Readonly<PhotonTraceStyle>;
}

interface StripVertex {
  x: number;
  y: number;
  nx: number;
  ny: number;
  u: number;
}

function stripVertices(pts: TraceRun, width: number, start: number, end: number, taperFraction: number): StripVertex[] {
  const span = Math.max(1e-6, end - start);
  const taperK = 1 / Math.max(1e-6, taperFraction);
  return pts.map((p, i) => {
    const a = pts[Math.max(0, i - 1)];
    const b = pts[Math.min(pts.length - 1, i + 1)];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const u = Math.max(0, Math.min(1, (p.t - start) / span));
    const halfWidth = (width * Math.sin(Math.min(1, u * taperK) * Math.PI / 2)) / 2;
    return { x: p.x, y: p.y, nx: (-dy / len) * halfWidth, ny: (dx / len) * halfWidth, u };
  });
}

/**
 * One strip = consecutive quads sharing exact edges (no round caps ⇒ no
 * beads). Segments are grouped into `shadeBands` age bands so each band is
 * ONE fill: bounded work (≤ bands fills per strip) with the same look.
 */
function drawStrip(
  ctx: CanvasRenderingContext2D,
  verts: StripVertex[],
  opacity: number,
  colourAt: (u: number) => RGB,
  style: Readonly<PhotonTraceStyle>,
): void {
  if (verts.length < 2) return;
  const bands = Math.max(1, Math.floor(style.shadeBands));
  let bandIndex = -1;
  let open = false;
  const closeBand = () => {
    if (!open) return;
    ctx.fill();
    open = false;
  };
  for (let i = 1; i < verts.length; i++) {
    const a = verts[i - 1];
    const b = verts[i];
    const u = (a.u + b.u) / 2;
    const band = Math.min(bands - 1, Math.floor(u * bands));
    if (band !== bandIndex) {
      closeBand();
      bandIndex = band;
      const uBand = (band + 0.5) / bands;
      ctx.fillStyle = rgba(colourAt(uBand), opacity * (0.13 + 0.87 * Math.pow(uBand, style.fadePower)));
      ctx.beginPath();
      open = true;
    }
    ctx.moveTo(a.x + a.nx, a.y + a.ny);
    ctx.lineTo(b.x + b.nx, b.y + b.ny);
    ctx.lineTo(b.x - b.nx, b.y - b.ny);
    ctx.lineTo(a.x - a.nx, a.y - a.ny);
    ctx.closePath();
  }
  closeBand();
}

/**
 * Draw one track's Photon Trace. Call once per independently identified
 * track, after the caller has cleared the canvas and applied its DPR
 * transform. `runs` must be in acquisition order with strictly increasing
 * time inside each run (see `prepareRuns`); coordinates are CSS pixels that
 * already include the same display/letterbox/mirror transform as the video.
 */
export function drawPhotonTrace(
  ctx: CanvasRenderingContext2D,
  runs: readonly TraceRun[],
  { time, startTime, headToleranceSeconds = 0, trackHex = null, style = PHOTON_TRACE_STYLE }: DrawPhotonTraceOptions,
): void {
  if (!Number.isFinite(time) || !Number.isFinite(startTime) || time < startTime) return;
  const { headRGB, tailRGB } = trackPalette(trackHex, style);
  const colourAt = (u: number) => mixRgb(tailRGB, headRGB, u);
  const visible: TraceRun[] = [];
  for (const run of runs) {
    const clipped = clipRun(run, startTime, time);
    if (clipped.length >= 2) visible.push(resampleRun(clipped, style.resampleSpacingPx, style.maxSamplesPerRun));
  }

  ctx.save();
  try {
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'screen';
    const passes: Array<[number, number, (u: number) => RGB]> = [
      [style.bloomPx, style.bloomAlpha, colourAt],
      [style.haloPx, style.haloAlpha, colourAt],
      [style.ribbonPx, style.ribbonAlpha, colourAt],
      [style.corePx, style.coreAlpha, () => style.coreRGB],
    ];
    for (const run of visible) {
      for (const [width, alpha, colour] of passes) {
        drawStrip(ctx, stripVertices(run, width, startTime, time, style.taperWindowFraction), alpha, colour, style);
      }
    }

    // Head light: only while a run is active at the current media time.
    // Never pin a glow to a stale last observation after tracking is lost.
    const active = runs.find((run) => run.length > 0 && run[0].t <= time && run[run.length - 1].t + headToleranceSeconds >= time);
    if (!active) return;
    const headT = Math.min(time, active[active.length - 1].t);
    const head = sampleRunAt(active, headT);
    const prev = sampleRunAt(active, Math.max(active[0].t, headT - 0.012));
    const r = style.tipRadiusPx;
    ctx.globalCompositeOperation = 'source-over';
    const glow = ctx.createRadialGradient(head.x, head.y, 0, head.x, head.y, r);
    glow.addColorStop(0, rgba(style.coreRGB, 1));
    glow.addColorStop(0.1, rgba(mixRgb(headRGB, style.coreRGB, 0.6), 1));
    glow.addColorStop(0.28, rgba(headRGB, 0.4));
    glow.addColorStop(1, rgba(headRGB, 0));
    ctx.fillStyle = glow;
    ctx.fillRect(head.x - r, head.y - r, r * 2, r * 2);
    if (Math.hypot(head.x - prev.x, head.y - prev.y) > 0.01) {
      ctx.translate(head.x, head.y);
      ctx.rotate(Math.atan2(head.y - prev.y, head.x - prev.x));
      const flare = ctx.createLinearGradient(-style.flareBackPx, 0, style.flareForwardPx, 0);
      flare.addColorStop(0, rgba(headRGB, 0));
      flare.addColorStop(0.75, rgba(style.coreRGB, 1));
      flare.addColorStop(1, rgba(style.coreRGB, 0));
      ctx.strokeStyle = flare;
      ctx.lineWidth = style.flareWidthPx;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(-style.flareBackPx, 0);
      ctx.lineTo(style.flareForwardPx, 0);
      ctx.stroke();
    }
  } finally {
    ctx.restore();
  }
}
